import BootstrapDialog from '@/components/Dialog/BootstrapDialog';
import { useGetVoicesSeats } from '@/components/Modals/CloneVoiceWizardModal/useGetVoicesSeats';
import { LocalVoiceFilters, VoiceFiltersEnum } from '@/hooks/useFilteredVoices';
import { useFiltersReset } from '@/hooks/useFiltersReset';
import { StyleCollection } from '@/utils/types';
import { IVoice } from '@d-id/types';
import { ReactNode, useEffect, useState } from 'react';
import { atom, useSetRecoilState } from 'recoil';
import { CloneVoiceContent } from './CloneVoiceContent/CloneVoiceContent';
import ImportVoiceContent from './ImportVoiceContent/ImportVoiceContent';
import { SelectVoiceContent, SubmitFooterType } from './SelectVoiceContent/SelectVoiceContent';

const styles: StyleCollection = {
    dialogButton: {
        fontSize: '1.6rem',
        alignItems: 'flex-start',
    },
    fieldButton: {
        whiteSpace: 'nowrap',
        fontSize: '14px',
        padding: '4px 6px',
    },
    dialogActions: {
        justifyContent: 'start !important',
    },
    dialogTitle: {
        height: '5rem',
        fontSize: '1.6rem',
    },
    closeIcon: theme => ({
        position: 'absolute',
        top: 16,
        right: 16,
        color: theme.colors.black.main,
    }),
    rootStyle: {
        borderRadius: '12px',
    },
};

export const ttsIsMutatingFavourites = atom<boolean>({
    key: 'tts-is-mutating-favourites',
    default: false,
});

const INITIAL_MODE = 'select';

interface Props {
    open: boolean;
    onClose: () => void;
    selectedVoice: IVoice | null;
    onSelectedVoiceChange?: (voice: IVoice) => void;
    SubmitFooter?: SubmitFooterType;
    selectVoicePinnedContent?: ReactNode;
    filters?: LocalVoiceFilters;
    hiddenFilters?: VoiceFiltersEnum[];
    showLowLatencyTag?: boolean;
    injectedVoices?: IVoice[];
}

export const VoiceModal = ({
    open,
    onClose,
    selectedVoice,
    onSelectedVoiceChange: onSelectedVoiceChange,
    SubmitFooter,
    selectVoicePinnedContent,
    filters,
    hiddenFilters,
    showLowLatencyTag,
    injectedVoices,
}: Props) => {
    const { reset } = useFiltersReset();
    const [mode, setMode] = useState<'select' | 'import' | 'clone-voice'>(INITIAL_MODE);
    const setIsMutating = useSetRecoilState(ttsIsMutatingFavourites);
    useGetVoicesSeats();

    useEffect(() => {
        if (!open) {
            setMode(INITIAL_MODE);
            setIsMutating(false);
        }
    }, [open]);

    const handleClose = () => {
        onClose();
        reset();
    };

    const onClone = () => {
        setMode(INITIAL_MODE);
        reset();
    };

    const handleModeChange = (mode: 'select' | 'import' | 'clone-voice') => {
        setMode(mode);
    };

    if (mode === 'clone-voice') {
        return <CloneVoiceContent setMode={handleModeChange} onClose={handleClose} onClone={onClone} />;
    }

    return (
        <BootstrapDialog onClose={handleClose} open={open} rootstyle={styles.rootStyle} width="91rem" data-testid="voice-modal">
            {mode === 'select' ? (
                <SelectVoiceContent
                    onClose={onClose}
                    setMode={handleModeChange}
                    selectedVoice={selectedVoice}
                    onSelectedVoiceChange={onSelectedVoiceChange}
                    SubmitFooter={SubmitFooter}
                    pinnedContent={selectVoicePinnedContent}
                    filters={filters}
                    hiddenFilters={hiddenFilters}
                    showLowLatencyTag={showLowLatencyTag}
                    injectedVoices={injectedVoices}
                />
            ) : (
                <ImportVoiceContent setMode={handleModeChange} />
            )}
        </BootstrapDialog>
    );
};
